'use client';

import { ProcessedNotification } from '@/types/api';
import { useState, useEffect } from 'react';

interface NotificationItemProps {
  notification: ProcessedNotification;
  index: number;
}

export default function NotificationItem({ notification, index }: NotificationItemProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), index * 100);
    return () => clearTimeout(timer);
  }, [index]);

  const AlertIcon = ({ severity }: { severity: string }) => {
    if (severity === 'high') {
      return (
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      );
    }
    if (severity === 'medium') {
      return (
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    }
    return (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    );
  };

  return (
    <li
      className={`py-3 flex items-start gap-3 transform transition-all duration-500 
        ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-4 opacity-0'}`}
    >
      <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${
        notification.severity === 'high' ? 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400' :
        notification.severity === 'medium' ? 'bg-yellow-50 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400' :
        'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400'
      }`}>
        <AlertIcon severity={notification.severity} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{notification.vessel}</span>
          {notification.is_hidden && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
              Hidden
            </span>
          )}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
          {notification.type} &middot; Expires: {new Date(notification.expireDate).toLocaleDateString()}
        </div>
      </div>
      <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap pt-0.5">{notification.time}</div>
    </li>
  );
}
